import { plural } from '../domain/format'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import defaults from '../data/catalog-sources.json'
import {
  REGISTRY_ID,
  isCatalogSource,
  loadSourceList,
  loadSources,
  type CatalogSource,
  type SourceResult,
} from '../domain/catalog'
import { parseRepositoryRef } from '../domain/repository'
import { planSync } from '../domain/sync'
import type { ReportsState } from './reports'
import { usePersistentState } from './viewState'
import { LAST_SYNC, PREFERENCE, readPreference, writePreference } from './prefs'

/**
 * The places to look for published assessments.
 *
 * Shipped defaults come from a data file; anything the user adds is kept
 * alongside them in the session. When repositories are added or refreshed,
 * DashVERSE-LW treats them as assessment registries and automatically
 * fetches newly published assessment results into the active session.
 */

/** Where followed sources are stored; a session import leaves these alone. */
export const SOURCES_KEY = 'catalog-sources'
const OVERRIDES_KEY = 'catalog-source-overrides'
/** The community sources the registry last listed, kept for offline starts. */
const COMMUNITY_KEY = 'catalog-community-sources'
export const CATALOG_KEYS = [SOURCES_KEY, OVERRIDES_KEY, COMMUNITY_KEY]

/** How long a fetched community list is used before it is fetched again. */
const COMMUNITY_MAX_AGE = 24 * 60 * 60 * 1000

interface CommunityList {
  fetchedAt: number
  sources: CatalogSource[]
}

const isCommunityList = (v: unknown): v is CommunityList =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as CommunityList).fetchedAt === 'number' &&
  Array.isArray((v as CommunityList).sources) &&
  (v as CommunityList).sources.every(isCatalogSource)

/** What a sync that brought something says. */
const fetchedMessage = (count: number) => `Fetched ${count} new ${plural(count, 'assessment')}.`

const SHIPPED = (defaults as { sources: CatalogSource[] }).sources

export type SyncInterval = 'off' | '15m' | '30m' | '1h' | '6h'

export const DEFAULT_SYNC_INTERVAL: SyncInterval = '15m'

export const SYNC_INTERVAL_LABELS: Record<SyncInterval, string> = {
  off: 'Manual only',
  '15m': 'Every 15 min',
  '30m': 'Every 30 min',
  '1h': 'Every hour',
  '6h': 'Every 6 hours',
}

const SYNC_INTERVAL_MS: Record<SyncInterval, number> = {
  off: 0,
  '15m': 15 * 60 * 1000,
  '30m': 30 * 60 * 1000,
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
}

function isOverrides(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  return Object.values(value).every(
    (o) =>
      typeof o === 'object' &&
      o !== null &&
      ['enabled', 'removed'].every((k) => {
        const flag = (o as Record<string, unknown>)[k]
        return flag === undefined || typeof flag === 'boolean'
      }),
  )
}

function isSyncable(result: SourceResult): boolean {
  return (
    result.source.id !== 'samples' &&
    result.source.kind !== 'urls' &&
    result.source.enabled !== false
  )
}

interface SourceOverride {
  enabled?: boolean
  removed?: boolean
}

export interface CatalogState {
  sources: CatalogSource[]
  results: SourceResult[]
  loading: boolean
  syncing: boolean
  lastSyncedAt: number | undefined
  syncMessage: string | undefined
  scheduledInterval: SyncInterval
  setScheduledInterval: (interval: SyncInterval) => void
  sync: () => Promise<{ newCount: number; total: number }>
  addRepository: (
    input: string,
    options?: { name?: string; ref?: string },
  ) => Promise<string | undefined>
  addIndex: (name: string, url: string) => void
  addUrlsSource: (name: string, urls: string[], description?: string) => void
  removeSource: (id: string) => void
  toggleSource: (id: string, enabled: boolean) => void
  restoreDefaultSources: () => void
}

export function useCatalog(reportsState?: ReportsState): CatalogState {
  const [added, setAdded, addedRestored] = usePersistentState<CatalogSource[]>(SOURCES_KEY, [], {
    debounceMs: 0,
    validate: (v) => Array.isArray(v) && v.every(isCatalogSource),
  })
  const [overrides, setOverrides, overridesRestored] = usePersistentState<
    Record<string, SourceOverride>
  >(OVERRIDES_KEY, {}, { debounceMs: 0, validate: isOverrides })
  const [community, setCommunity, communityRestored] = usePersistentState<CommunityList>(
    COMMUNITY_KEY,
    { fetchedAt: 0, sources: [] },
    { debounceMs: 0, validate: isCommunityList },
  )
  const restored = addedRestored && overridesRestored && communityRestored

  const [results, setResults] = useState<SourceResult[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | undefined>()

  const [lastSyncedAt, setLastSyncedAt] = useState<number | undefined>(() => {
    const stored = Number(readPreference(LAST_SYNC))
    return stored > 0 ? stored : undefined
  })

  const [scheduledInterval, setScheduledIntervalState] = useState<SyncInterval>(() => {
    const stored = readPreference(PREFERENCE.syncInterval)
    return stored && Object.hasOwn(SYNC_INTERVAL_LABELS, stored)
      ? (stored as SyncInterval)
      : DEFAULT_SYNC_INTERVAL
  })

  const setScheduledInterval = useCallback((interval: SyncInterval) => {
    setScheduledIntervalState(interval)
    writePreference(PREFERENCE.syncInterval, interval)
  }, [])

  const sources = useMemo(() => {
    // 1. Shipped sources with user overrides applied
    const shippedSources: CatalogSource[] = SHIPPED.filter(
      (source) => overrides[source.id]?.removed !== true,
    ).map((source) => {
      const isUrls = source.kind === 'urls'
      const urls = isUrls
        ? source.urls?.map((u) =>
            u.startsWith('http://') || u.startsWith('https://') || u.startsWith('/')
              ? u
              : `${import.meta.env.BASE_URL}${u}`,
          )
        : source.urls

      return { ...source, urls, enabled: overrides[source.id]?.enabled ?? source.enabled ?? true }
    })

    // 2. Custom user-added sources with user overrides applied
    const customSources: CatalogSource[] = added
      .filter((source) => overrides[source.id]?.removed !== true)
      .map((source) => ({
        ...source,
        enabled: overrides[source.id]?.enabled ?? source.enabled ?? true,
      }))

    // 3. Community sources the registry lists: always off until the user turns
    // one on, and never in place of a source that is already here.
    const taken = new Set([...shippedSources, ...customSources].map((source) => source.id))
    const communitySources: CatalogSource[] = community.sources
      .filter((source) => !taken.has(source.id) && overrides[source.id]?.removed !== true)
      .map((source) => ({ ...source, enabled: overrides[source.id]?.enabled ?? false }))

    return [...shippedSources, ...customSources, ...communitySources]
  }, [added, overrides, community])

  /**
   * The registry's list of community sources, fetched by itself (the list
   * only names sources; nothing is fetched from them until one is enabled).
   * At most once a day, and again on every sync. A list that cannot be read
   * leaves the last one in place.
   */
  const refreshCommunity = useCallback(
    async (force: boolean) => {
      const registry = SHIPPED.find((source) => source.id === REGISTRY_ID)
      if (!registry) return
      if (!force && Date.now() - community.fetchedAt < COMMUNITY_MAX_AGE) return
      try {
        const listed = await loadSourceList(registry)
        setCommunity({ fetchedAt: Date.now(), sources: listed })
      } catch {
        // Not published yet, or the network is down: keep what was there.
      }
    },
    [community.fetchedAt, setCommunity],
  )

  useEffect(() => {
    if (communityRestored) void refreshCommunity(false)
  }, [communityRestored, refreshCommunity])

  const syncingRef = useRef(false)
  const reportsStateRef = useRef(reportsState)
  useEffect(() => {
    reportsStateRef.current = reportsState
  }, [reportsState])

  /**
   * Fetches whatever the loaded sources list that this session lacks.
   *
   * Shared by an explicit sync and by the automatic load after sources change,
   * so both apply the same rules: never the bundled samples or plain URL
   * lists, never a disabled source, never a report the user removed.
   */
  const ingestNew = useCallback(async (nextResults: SourceResult[]) => {
    const allEntries = nextResults.filter(isSyncable).flatMap((r) => r.entries)
    const live = reportsStateRef.current
    // Not before the session and the removals are read: planned against an
    // empty session, a sync would fetch everything, removed reports included.
    if (!live?.ready) return { newCount: 0, total: 0 }
    const plan = planSync(allEntries, live.reports, live.dismissed)
    if (plan.newEntries.length > 0) {
      await live.addFromUrls(
        plan.newEntries.map((e) => e.reportUrl),
        { fromSync: true },
      )
    }
    return { newCount: plan.newEntries.length, total: allEntries.length }
  }, [])

  /**
   * Syncs all catalog repositories and automatically fetches new assessment documents
   * into the active session & IndexedDB.
   */
  const sync = useCallback(async () => {
    if (syncingRef.current) return { newCount: 0, total: 0 }
    syncingRef.current = true
    setSyncing(true)

    try {
      void refreshCommunity(true)
      const nextResults = await loadSources(sources)
      setResults(nextResults)
      const { newCount, total } = await ingestNew(nextResults)
      setSyncMessage(newCount > 0 ? fetchedMessage(newCount) : 'Every source is up to date.')

      const now = Date.now()
      setLastSyncedAt(now)
      writePreference(LAST_SYNC, String(now))

      return { newCount, total }
    } catch {
      setSyncMessage('Could not complete sync. The network may be unavailable.')
      return { newCount: 0, total: 0 }
    } finally {
      syncingRef.current = false
      setSyncing(false)
      setLoading(false)
    }
  }, [sources, ingestNew, refreshCommunity])

  /** Initial load when sources are restored from storage */
  useEffect(() => {
    if (!restored) return
    let cancelled = false

    void loadSources(sources).then((next) => {
      if (cancelled) return
      setResults(next)
      setLoading(false)
      // Also how a newly added source gets its assessments: adding one
      // changes the sources, which runs this.
      void ingestNew(next).then(({ newCount }) => {
        if (!cancelled && newCount > 0) {
          setSyncMessage(fetchedMessage(newCount))
        }
      })
    })

    return () => {
      cancelled = true
    }
  }, [restored, sources, ingestNew])

  // Sources may finish loading before the session is read, in which case the
  // automatic ingest above skipped. Run it once when the session is ready.
  const ready = reportsState?.ready ?? false
  const wasReady = useRef(ready)
  useEffect(() => {
    if (!ready || wasReady.current) return
    wasReady.current = true
    if (results.length > 0) {
      void ingestNew(results).then(({ newCount }) => {
        if (newCount > 0) {
          setSyncMessage(fetchedMessage(newCount))
        }
      })
    }
  }, [ready, results, ingestNew])

  /** Scheduled periodic synchronization timer */
  useEffect(() => {
    if (scheduledInterval === 'off') return
    const intervalMs = SYNC_INTERVAL_MS[scheduledInterval]
    if (!intervalMs) return

    const timer = setInterval(() => {
      void sync()
    }, intervalMs)

    return () => clearInterval(timer)
  }, [scheduledInterval, sync])

  /** Adds a source, or replaces one with the same id, clearing any earlier removal of it. */
  const put = useCallback(
    (source: CatalogSource) => {
      setOverrides((prev) => {
        const next = { ...prev }
        delete next[source.id]
        return next
      })
      setAdded((prev) => [...prev.filter((s) => s.id !== source.id), source])
    },
    [setOverrides, setAdded],
  )

  const addRepository = useCallback(
    async (
      input: string,
      options?: { name?: string; ref?: string },
    ): Promise<string | undefined> => {
      const parsed = parseRepositoryRef(input)
      if (!parsed) {
        return 'That does not look like a repository. Try "owner/name" or a GitHub / GitLab address.'
      }

      const id = `repo:${parsed.provider}:${parsed.host}:${parsed.repository}`
      if (sources.some((source) => source.id === id || source.repository === parsed.repository)) {
        return 'That repository is already listed.'
      }

      const newSource: CatalogSource = {
        id,
        name: options?.name || parsed.repository,
        kind: 'repository',
        provider: parsed.provider,
        host: parsed.host,
        repository: parsed.repository,
        ref: options?.ref || parsed.ref || (parsed.provider === 'gitlab' ? 'main' : 'HEAD'),
        enabled: true,
        lastSyncedAt: Date.now(),
      }

      put(newSource)

      return undefined
    },
    [sources, put],
  )

  const addIndex = useCallback(
    (name: string, url: string) => {
      const id = `index:${url}`
      const newSource: CatalogSource = {
        id,
        name: name.trim() || url,
        kind: 'index',
        url: url.trim(),
        enabled: true,
      }
      put(newSource)
    },
    [put],
  )

  const addUrlsSource = useCallback(
    (name: string, urls: string[], description?: string) => {
      const id = `urls:${Date.now()}`
      const newSource: CatalogSource = {
        id,
        name: name.trim() || 'Custom URLs',
        kind: 'urls',
        urls,
        description,
        enabled: true,
      }
      put(newSource)
    },
    [put],
  )

  const removeSource = useCallback(
    (id: string) => {
      setOverrides((prev) => ({
        ...prev,
        [id]: { ...prev[id], removed: true },
      }))
      setAdded((prev) => prev.filter((source) => source.id !== id))
    },
    [setOverrides, setAdded],
  )

  const toggleSource = useCallback(
    (id: string, enabled: boolean) => {
      setOverrides((prev) => ({
        ...prev,
        [id]: { ...prev[id], enabled, removed: false },
      }))
      setAdded((prev) => prev.map((source) => (source.id === id ? { ...source, enabled } : source)))
    },
    [setOverrides, setAdded],
  )

  const restoreDefaultSources = useCallback(() => {
    setOverrides({})
  }, [setOverrides])

  return {
    sources,
    results,
    loading,
    syncing,
    lastSyncedAt,
    syncMessage,
    scheduledInterval,
    setScheduledInterval,
    sync,
    addRepository,
    addIndex,
    addUrlsSource,
    removeSource,
    toggleSource,
    restoreDefaultSources,
  }
}
